// Abort a stream if no data is received for 5 minutes
export const STREAM_IDLE_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * How a streaming request ended. "eof" only means the connection closed cleanly — it does
 * not mean the reply finished; callers that care look for the protocol's own end event.
 */
export type StreamEndReason = "eof" | "aborted" | "network_error";

export interface StreamOutcome {
  reason: StreamEndReason;
  /** False when the request failed before any response arrived, so the server may never have seen it. */
  responseStarted: boolean;
}

/**
 * The proxy lost the backend before it answered (502) or gave up waiting (504), so the request
 * may or may not have been taken. A 503 is a deliberate refusal and stays an error.
 */
export function isGatewayError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 502 || status === 504;
}

/** No bytes arrived for STREAM_IDLE_TIMEOUT_MS. Keeps the message the untyped error used to carry. */
export class StreamIdleTimeoutError extends Error {
  constructor() {
    super("Error in streaming request: idle_timeout");
    this.name = "StreamIdleTimeoutError";
  }
}

function createSseLineHandler(onChunk: (chunk: string) => void) {
  let eventDataLines: string[] = [];

  const flushEvent = () => {
    if (eventDataLines.length > 0) {
      onChunk(eventDataLines.join("\n"));
      eventDataLines = [];
    }
  };

  return {
    handleLine(line: string) {
      if (line.startsWith("data:")) {
        eventDataLines.push(line.replace(/^data: ?/, ""));
      } else if (line === "") {
        flushEvent();
      } else {
        flushEvent();
        onChunk(line + "\n");
      }
    },
    flush: flushEvent,
  };
}

interface StreamRequestOptions {
  url: string;
  method: "GET" | "POST";
  body?: unknown;
  onChunk: (chunk: string) => void;
  signal?: AbortSignal;
  /** Reject a bad response (auth, HTTP errors) before its body is read. */
  checkResponse?: (response: Response) => Promise<void>;
  /** Bytes arrived, even mid-event: a large frame on a slow link is progress before its line completes. */
  onActivity?: () => void;
  idleTimeoutMs?: number;
}

/**
 * Run a streaming fetch and hand each SSE event to `onChunk`. Resolves with how the stream
 * ended — clean close, caller abort or dropped connection — and rejects only for a bad
 * response (through `checkResponse`) or the idle timeout.
 */
export async function streamRequest({
  url,
  method,
  body,
  onChunk,
  signal,
  checkResponse,
  onActivity,
  idleTimeoutMs = STREAM_IDLE_TIMEOUT_MS,
}: StreamRequestOptions): Promise<StreamOutcome> {
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let idleTimedOut = false;
  let responseStarted = false;

  // If an external signal is provided, abort our internal controller when it fires
  if (signal?.aborted) {
    controller.abort();
  } else {
    signal?.addEventListener("abort", abortFromCaller, { once: true });
  }

  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      idleTimedOut = true;
      controller.abort();
    }, idleTimeoutMs);
  };

  try {
    const response = await fetch(url, {
      method,
      signal: controller.signal,
      credentials: "same-origin",
      ...(method === "POST"
        ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
        : {}),
    });
    responseStarted = true;
    await checkResponse?.(response);

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No response body");
    }

    const decoder = new TextDecoder();
    // Buffer incomplete lines so large events (e.g. tool_result with big content) split across TCP chunks are not dropped
    let lineBuffer = "";
    const handler = createSseLineHandler(onChunk);

    // Start idle timer once the connection is open
    resetIdleTimer();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Reset idle timer on every chunk received
      resetIdleTimer();
      onActivity?.();

      lineBuffer += decoder.decode(value, { stream: true });
      const lines = lineBuffer.split("\n");
      // Keep the last segment in buffer (may be incomplete until next chunk)
      lineBuffer = lines.pop() ?? "";

      for (const line of lines) {
        handler.handleLine(line);
      }
    }

    // Flush any remaining data (last line may not end with \n)
    lineBuffer += decoder.decode();
    if (lineBuffer.trim()) {
      handler.handleLine(lineBuffer);
    }
    handler.flush();
    return { reason: "eof", responseStarted };
  } catch (error) {
    if (idleTimedOut) {
      throw new StreamIdleTimeoutError();
    }
    // Caller aborts and dropped connections resolve: the outcome tells callers which one it was.
    if (controller.signal.aborted || (error instanceof Error && error.name === "AbortError")) {
      return { reason: "aborted", responseStarted };
    }
    if (error instanceof Error && error.name === "TypeError") {
      return { reason: "network_error", responseStarted };
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Error in streaming request: ${error}`);
  } finally {
    if (idleTimer) clearTimeout(idleTimer);
    signal?.removeEventListener("abort", abortFromCaller);
  }
}
